import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { css } from "glamor";
import { Message, Icon } from "semantic-ui-react";
import { colors, spacing } from "../library/theme";
import Modal from "../library/Modal";
import CardBrandIcon from "./CardBrandIcon";
import ChangeCardFormContainer from "./ChangeCardFormContainer";

const modalClass = css({
  borderRadius: 14,
  "&.ui.tiny.modal": {
    maxWidth: "440px",
  },
  ".actions": {
    padding: "16px 20px 20px !important",
    background: "#FFFFFF !important",
    borderTop: "none !important",
  },
});

const headerWrapper = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const headerLeft = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const headerTitle = css({
  fontFamily: "Montserrat, sans-serif !important",
  fontSize: 16,
  fontWeight: 600,
  margin: 0,
  color: "#0F172A",
});

const closeButton = css({
  cursor: "pointer",
  color: colors.greyDark,
  fontSize: 18,
  ":hover": { color: colors.black },
});

const backButton = css({
  cursor: "pointer",
  color: colors.greyDark,
  fontSize: 16,
  ":hover": { color: colors.black },
});

const savedCardItem = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.s,
  padding: "12px 14px",
  border: "0.612px solid #E2E8F0",
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.2s",
  marginBottom: spacing.s,
  ":hover": {
    borderColor: "#0369A1",
  },
});

const savedCardItemSelected = css({
  borderColor: "#0369A1",
  backgroundColor: "#F0F9FF",
});

const savedCardLabel = css({
  flex: 1,
  fontSize: 13,
  fontWeight: 500,
  color: "#0F172A",
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
});

const defaultBadge = css({
  fontSize: 9,
  fontWeight: 500,
  color: "#0369A1",
  background: "#E0F2FE",
  borderRadius: 3,
  padding: "1px 5px",
});

const addMethodLink = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.xxs,
  padding: `${spacing.s} ${spacing.m}`,
  border: "0.612px dashed #CBD5E1",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 12,
  color: "#64748B",
  backgroundColor: "transparent",
  width: "100%",
  transition: "all 0.2s",
  ":hover": {
    borderColor: "#0369A1",
    color: "#0369A1",
  },
});

const confirmButtonClass = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: 40,
  borderRadius: 8,
  border: "none",
  background: "#0F172A",
  color: "#FFFFFF",
  fontFamily: "Montserrat, sans-serif",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "19.5px",
  cursor: "pointer",
  ":hover": { background: "#1E293B" },
  ":disabled": { background: "#94A3B8", cursor: "not-allowed" },
});

const radioStyle = { accentColor: "#0369A1" };

const errorMessageStyle = { marginBottom: spacing.m };

function brandLabel(brand) {
  const b = brand?.toLowerCase();
  if (b === "visa") return "Visa";
  if (b === "mastercard") return "Mastercard";
  if (b === "elo") return "Elo";
  if (b === "amex") return "Amex";
  return brand || "Cartão";
}

function SavedCardItem({ card, isSelected, onSelect }) {
  const handleClick = useCallback(() => onSelect(card.id), [card.id, onSelect]);

  return (
    <div
      className={css(savedCardItem, isSelected && savedCardItemSelected)}
      onClick={handleClick}
    >
      <input type="radio" checked={isSelected} readOnly style={radioStyle} />
      <CardBrandIcon brand={card.brand} size={22} />
      <span className={savedCardLabel}>
        {brandLabel(card.brand)} ****{card.number}
      </span>
      {card.default && <span className={defaultBadge}>Principal</span>}
    </div>
  );
}

export default function RetryChargeModal({
  open,
  onClose,
  creditCardTokens = [],
  onConfirm,
  onNewCardSubmit,
  loading,
  showSetAsDefault,
}) {
  const [screen, setScreen] = useState("list");
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const formResetRef = useRef(null);

  const defaultCardId = useMemo(() => {
    const defaultCard = creditCardTokens.find((c) => c.default);
    return defaultCard?.id || creditCardTokens[0]?.id || null;
  }, [creditCardTokens]);

  const effectiveSelectedId =
    selectedCardId !== null ? selectedCardId : defaultCardId;
  const handleShowNewCardForm = useCallback(() => setScreen("newCard"), []);

  useEffect(() => {
    if (selectedCardId === null && defaultCardId !== null) {
      setSelectedCardId(defaultCardId);
    }
  }, [defaultCardId, selectedCardId]);

  const handleClose = useCallback(() => {
    if (formResetRef.current) formResetRef.current();
    setScreen("list");
    setSelectedCardId(null);
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const handleBack = useCallback(() => {
    if (formResetRef.current) formResetRef.current();
    setScreen("list");
    setSubmitError(null);
  }, []);

  const handleConfirmSelection = useCallback(() => {
    if (!effectiveSelectedId) return;
    const card = creditCardTokens.find((c) => c.id === effectiveSelectedId);
    if (!card) return;

    setSubmitError(null);
    onConfirm(card.cardId).catch((error) => {
      const msg =
        error?.response?.body?.errors?.[0] ||
        error?.response?.body?.error ||
        "Ocorreu um erro ao gerar a cobrança. Tente novamente mais tarde.";
      setSubmitError(msg);
    });
  }, [effectiveSelectedId, creditCardTokens, onConfirm]);

  const handleNewCardSubmit = useCallback(
    (cardData) => {
      setSubmitError(null);
      return onNewCardSubmit(cardData)
        .then(() => {
          if (formResetRef.current) formResetRef.current();
          setScreen("list");
          setSelectedCardId(null);
        })
        .catch((error) => {
          const msg =
            error?.response?.body?.errors?.[0] ||
            error?.response?.body?.error ||
            "Ocorreu um erro ao processar o cartão. Tente novamente mais tarde.";
          setSubmitError(msg);
        });
    },
    [onNewCardSubmit],
  );

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose} className={modalClass} size="tiny">
      <Modal.Header>
        <div className={headerWrapper}>
          <div className={headerLeft}>
            {screen === "newCard" && (
              <Icon
                name="arrow left"
                className={backButton}
                onClick={handleBack}
              />
            )}
            <h2 className={headerTitle}>
              {screen === "list"
                ? "Escolher forma de pagamento"
                : "Novo método de pagamento"}
            </h2>
          </div>
          <Icon name="close" className={closeButton} onClick={handleClose} />
        </div>
      </Modal.Header>

      <Modal.Content>
        {submitError && (
          <Message
            error
            visible
            content={submitError}
            style={errorMessageStyle}
          />
        )}

        {screen === "list" ? (
          <>
            {creditCardTokens.map((card) => (
              <SavedCardItem
                key={card.id}
                card={card}
                isSelected={card.id === effectiveSelectedId}
                onSelect={setSelectedCardId}
              />
            ))}

            <button
              type="button"
              className={addMethodLink}
              onClick={handleShowNewCardForm}
            >
              + Adicionar novo método
            </button>
          </>
        ) : (
          <ChangeCardFormContainer
            onSubmit={handleNewCardSubmit}
            loading={loading}
            resetRef={formResetRef}
            showSetAsDefault={showSetAsDefault}
          />
        )}
      </Modal.Content>

      {screen === "list" && (
        <Modal.Actions>
          <button
            type="button"
            className={confirmButtonClass}
            onClick={handleConfirmSelection}
            disabled={loading || !effectiveSelectedId}
          >
            {loading ? "Aguarde..." : "Confirmar tentativa de cobrança"}
          </button>
        </Modal.Actions>
      )}
    </Modal>
  );
}
